precision highp float;

uniform float time;
uniform float brightness;
uniform float progress;

varying float vAlpha;
varying float vRandom;

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);

  float a = hash(i);
  float b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0));
  float d = hash(i + vec2(1.0, 1.0));

  vec2 u = f * f * f * (f * (f * 6.0 - 15.0) + 10.0);

  return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
}

float fbm(vec2 p) {
  float v   = 0.0;
  float amp = 0.5;
  for (int i = 0; i < 3; i++) {
    v   += amp * noise(p);
    p   *= 2.1; 
    amp *= 0.48;
  }
  return v;
}

float questionMarkSDF(vec2 p) {
  vec2  arcPos     = p - vec2(0.0, 0.18);
  float r          = length(arcPos);
  float arc        = max(abs(r - 0.18) - 0.06,
                         arcPos.y < -0.05 ? arcPos.x : -1.0);
  float centralBar = max(abs(p.x) - 0.06, abs(p.y + 0.02) - 0.12);
  float dotDist    = length(p - vec2(0.0, -0.38)) - 0.07;

  return min(min(arc, centralBar), dotDist);
}

void main() {
  vec2 uv   = vec2(0.5 - gl_PointCoord.x, 0.5 - gl_PointCoord.y) * 1.1;
  float dist = questionMarkSDF(uv);

  float charMask = smoothstep(0.025, -0.025, dist);

  float halo = smoothstep(0.08, 0.0, dist) * (1.0 - charMask);
  halo *= 0.35;

  float edgeGlow = exp(-abs(dist) * 28.0) * 0.9;

  vec2  ncoord       = uv * 4.5;
  float timeDrift    = time * 0.12 + vRandom * 10.0;
  float n1 = fbm(ncoord + vec2(timeDrift,        time * 0.06));
  float n2 = fbm(ncoord * 1.4 + vec2(-timeDrift * 0.5, time * 0.09));
  float n  = mix(n1, n2, 0.4);

  float internalNoise = mix(0.70, 1.15, clamp(n, 0.0, 1.0));

  float alpha = charMask * internalNoise * vAlpha
              + halo * vAlpha
              + edgeGlow * charMask * vAlpha * 0.6;

  if (alpha < 0.08) discard;

  float p = clamp(progress, 0.0, 1.0);
  float b = max(brightness, 0.0);

  vec3 colDeep    = vec3(0.00, 0.02, 0.18);   
  vec3 colMid     = vec3(0.02, 0.18, 0.72);
  vec3 colBright  = vec3(0.10, 0.55, 1.00);
  vec3 colIce     = vec3(0.55, 0.85, 1.00);

  float t1 = clamp(vRandom * 0.8 + 0.2, 0.0, 1.0);
  vec3  midRange = mix(colDeep, colMid, t1);
  vec3  topRange = mix(colBright, colIce, clamp(n * 0.6 + 0.4, 0.0, 1.0));
  vec3  baseCol  = mix(midRange, topRange, clamp(internalNoise - 0.7, 0.0, 1.0));

  vec3 glowCol = mix(colBright, colIce, 0.7);

  vec3 color = mix(baseCol, glowCol, edgeGlow * 0.55);

  color += colMid * halo * 0.6;

  color *= p * (1.0 + b * 0.35);
  color = pow(clamp(color, 0.0, 1.0), vec3(0.92));

  float finalAlpha = clamp(alpha * p * vAlpha, 0.0, 1.0);

  gl_FragColor = vec4(color, finalAlpha);
}
